/**
 * @file sysdata-mixin.ts
 *
 * Exposes `<as-app>.sysData` — the read-only counterpart to `appData`.
 *
 *   appData  — the app's own state, persisted with the project, read/write
 *   sysData  — the library's state, never persisted, read-only
 *
 * Both the object and the store are store/sysDataStore.ts. This mixin only
 * hands `sysData` over under a name apps expect and starts the store's event
 * wiring on connect — `app.sysData` and the `sysData` export are one object.
 *
 * Exposes: sysData
 */
import { type Constructor } from './mixin-types-utils.ts';
import { initSysData, sysData, type SysDataAPI } from '../../../store/sysDataStore.ts';

// Re-exported: this is where consumers have always found the type.
export type { SysDataAPI };

/** Surface added by SysDataMixin. */
export interface SysDataMixinMembers {
  /** Read-only view of what the library is doing right now — lifecycle flags,
   *  session/app identity, current project. The same object as the module-level
   *  `sysData` export; the element doesn't own it. See `SysData`. */
  readonly sysData: SysDataAPI;
}

export const SysDataMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class SysDataMixin extends Base {
    _sysDataTeardown: (() => void) | null = null;

    /** The module-level `sysData`, unchanged. On the element because that is
     *  where apps look for it, not because the element owns it. */
    get sysData(): SysDataAPI {
      return sysData;
    }

    async connectedCallback() {
      // Before the super chain, not after: several mixins above this one have
      // async connectedCallbacks, so awaiting first would leave the store deaf
      // for a microtask or more. Nothing here depends on the rest of the chain.
      this._sysDataTeardown = initSysData(this);
      await super.connectedCallback?.();
    }

    disconnectedCallback() {
      this._sysDataTeardown?.();
      this._sysDataTeardown = null;
      super.disconnectedCallback?.();
    }
  };
